import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../auth/authContext";
import { BackendAPI } from "../../../const/api";
import request from "../../../api/request";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useToast } from "../../../component/ToastBar";
import {FieldType, UserRole} from "../../../const/enum";
import { MaterialAttributeField } from "../../../const/model";

const fieldTypeMap = {
  NUMBER: "数字",
  PERCENTAGE: "百分数",
  TEXT: "文字",
  URL: "链接",
};

const MaterialAttributeConfig = () => {
  const [attributes, setAttributes] = useState<MaterialAttributeField[]>([]);
  const [defaultPriceFieldId, setDefaultPriceFieldId] = useState<number | "">(
    "",
  );
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editedAttributes, setEditedAttributes] =
    useState<MaterialAttributeField | null>(null);
  const toast = useToast();
  const authContext = useContext(AuthContext);
  const canManage =
    !!authContext && (authContext.isFinance() || authContext.isAdmin());

  const fetchAttributes = useCallback(async () => {
    try {
      const response = await request.get(BackendAPI.getAttributeFields);
      if (!response.success) {
        console.error("Error fetching material attributes:", response.message);
      }
      return response.data.attributes;
    } catch (error) {
      console.error("Error fetching material attributes:", error);
      return [];
    }
  }, []);

  const fetchDefaultPriceField = useCallback(async () => {
    try {
      const response = await request.get(BackendAPI.getDefaultPriceAttributeField);
      if (!response.success) {
        return "";
      }
      const fieldId = response.data?.fieldId;
      return fieldId ?? "";
    } catch (error) {
      console.error("Error fetching default price field:", error);
      return "";
    }
  }, []);

  const updateDefaultPriceField = useCallback(
    async (fieldId: number | "") => {
      const response = await request.put(BackendAPI.setDefaultPriceAttributeField, {
        fieldId: fieldId === "" ? null : fieldId,
      });
      if (!response.success) {
        return;
      }
      setDefaultPriceFieldId(response.data?.fieldId ?? "");
      toast("默认价格列设置成功");
      fetchAttributes().then((res) => setAttributes(res));
    },
    [fetchAttributes, toast],
  );

  useEffect(() => {
    fetchAttributes().then((res) => setAttributes(res));
    fetchDefaultPriceField().then((fieldId) => setDefaultPriceFieldId(fieldId));
  }, [fetchAttributes, fetchDefaultPriceField]);

  // Start editing a row
  const onEdit = (record: MaterialAttributeField) => {
    setEditingKey(record.id);
    setEditedAttributes({ ...record });
  };

  // Handle save
  const onSave = async () => {
    try {
      if (!editedAttributes) {
        return;
      }
      if (editedAttributes.id === -1) {
        await request.put(BackendAPI.addAttributeField, editedAttributes);
      } else {
        await request.post(BackendAPI.updateAttributeField, editedAttributes);
      }
      toast("保存成功");
      setEditingKey(null);
      fetchAttributes().then((res) => setAttributes(res));
    } catch (error) {
      toast("保存出错", "error");
      console.error("Error saving material attributes:", error);
    }
  };

  const onDelete = async (record: MaterialAttributeField) => {
    if (record.id === -1) {
      setAttributes(attributes.filter((attr) => attr.id !== -1));
      return;
    }
    if (record.deletable) {
      try {
        await request.del(BackendAPI.deleteAttributeField, {
          id: record.id,
        });
        toast("删除成功");
        fetchAttributes().then((res) => setAttributes(res));
      } catch (error) {
        toast("删除出错", "error");
        console.error("Error deleting material attributes:", error);
      }
    } else {
      toast("不可删除", "error");
    }
  };

  // Handle input change
  const handleInputChange = (
    field: keyof MaterialAttributeField,
    value: any,
  ) => {
    if (editedAttributes) {
      setEditedAttributes({ ...editedAttributes, [field]: value });
    }
  };

  const handleRolesCheckboxChange = (
    field: "viewRoles" | "editRoles",
    role: string,
  ) => {
    if (editedAttributes) {
      const roles = editedAttributes[field];
      const updatedRoles = roles.includes(role)
        ? roles.filter((r) => r !== role)
        : [...roles, role];
      setEditedAttributes({ ...editedAttributes, [field]: updatedRoles });
    }
  };

  return (
    <TableContainer component={Paper}>
      {canManage && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            sx={{ px: 3, fontWeight: 600 }}
            onClick={() => {
              const newField = {
                id: -1,
                name: "",
                description: "",
                fieldType: FieldType.NUMBER,
                viewRoles: [],
                editRoles: [],
                deletable: false,
              };
              setAttributes([...attributes, newField]);
              onEdit(newField);
            }}
            disabled={editingKey !== null}
          >
            新增
          </Button>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="default-price-field-select-label">
              订单默认价格列
            </InputLabel>
            <Select
              labelId="default-price-field-select-label"
              label="订单默认价格列"
              value={defaultPriceFieldId}
              disabled={editingKey !== null}
              onChange={(e) => {
                const value =
                  e.target.value === "" ? "" : Number(e.target.value);
                setDefaultPriceFieldId(value);
                updateDefaultPriceField(value).catch(() =>
                  toast("设置默认价格列失败", "error"),
                );
              }}
            >
              <MenuItem value="">无</MenuItem>
              {attributes.map((field) => (
                <MenuItem key={field.id} value={field.id}>
                  {field.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>列名</TableCell>
            <TableCell>描述</TableCell>
            <TableCell>数字类型</TableCell>
            <TableCell>查看权限</TableCell>
            <TableCell>编辑权限</TableCell>
            <TableCell>可删除</TableCell>
            <TableCell>操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attributes.map((row) => (
            <TableRow key={row.id}>
              {/*名称*/}
              <TableCell>
                {editingKey === row.id ? (
                  <TextField
                    value={editedAttributes?.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    variant="standard"
                  />
                ) : (
                  row.name
                )}
              </TableCell>
              {/*描述*/}
              <TableCell>
                {editingKey === row.id ? (
                  <TextField
                    value={editedAttributes?.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    variant="standard"
                  />
                ) : (
                  row.description
                )}
              </TableCell>
              {/*类型*/}
              <TableCell>
                {editingKey === row.id ? (
                  <Select
                    value={editedAttributes?.fieldType}
                    onChange={(e) =>
                      handleInputChange("fieldType", e.target.value)
                    }
                    variant="standard"
                  >
                    {Object.keys(FieldType).map((type) => {
                      return (
                        <MenuItem value={type}>{fieldTypeMap[type]}</MenuItem>
                      );
                    })}
                  </Select>
                ) : (
                  fieldTypeMap[row.fieldType]
                )}
              </TableCell>
              {/*查看权限*/}
              <TableCell>
                {editingKey === row.id ? (
                  <FormControl component="fieldset">
                    <FormGroup>
                      {Object.keys(UserRole)
                        .filter((r) => r !== "ADMIN")
                        .map((role) => (
                          <FormControlLabel
                            key={role}
                            control={
                              <Checkbox
                                checked={editedAttributes?.viewRoles.includes(
                                  role,
                                )}
                                onChange={() =>
                                  handleRolesCheckboxChange("viewRoles", role)
                                }
                              />
                            }
                            label={UserRole[role]}
                          />
                        ))}
                    </FormGroup>
                  </FormControl>
                ) : (
                  row.viewRoles.map((role) => UserRole[role]).join(", ")
                )}
              </TableCell>
              {/*编辑权限*/}
              <TableCell>
                {editingKey === row.id ? (
                  <FormControl component="fieldset">
                    <FormGroup>
                      {["FINANCE", "PURCHASER"].map((role) => (
                        <FormControlLabel
                          key={role}
                          control={
                            <Checkbox
                              checked={editedAttributes?.editRoles.includes(
                                role,
                              )}
                              onChange={() =>
                                handleRolesCheckboxChange("editRoles", role)
                              }
                            />
                          }
                          label={UserRole[role]}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                ) : (
                  row.editRoles.map((role) => UserRole[role]).join(", ")
                )}
              </TableCell>
              {/*可删除*/}
              <TableCell>
                {editingKey === row.id ? (
                  <Switch
                    value={editedAttributes?.deletable}
                    checked={editedAttributes?.deletable}
                    onChange={(e) =>
                      handleInputChange("deletable", e.target.checked)
                    }
                  />
                ) : row.deletable ? (
                  "是"
                ) : (
                  "否"
                )}
              </TableCell>
              {/*操作*/}
              <TableCell>
                {editingKey === row.id ? (
                  <Button onClick={() => onSave()}>保存</Button>
                ) : (
                  <Button onClick={() => onEdit(row)}>编辑</Button>
                )}
                {editingKey === row.id ? (
                  <Button onClick={() => setEditingKey(null)}>取消</Button>
                ) : (
                  <Button onClick={() => onDelete(row)}>删除</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MaterialAttributeConfig;
